export const EDIT_TOOLBAR_GAP_PX = 6
export const EDIT_TOOLBAR_MORE_RESERVE_PX = 36

export const EDIT_OVERFLOW_DROP_ORDER = [
  "copy",
  "download",
  "redo",
  "undo",
  "text",
  "arrowLabel",
  "arrow",
  "shape",
  "eraser",
] as const

export type EditOverflowKey = (typeof EDIT_OVERFLOW_DROP_ORDER)[number]

export type EditToolbarMeasuredWidths = {
  icon: number
  format: number
  resolution: number
  divider: number
}

export const FALLBACK_EDIT_TOOLBAR_WIDTHS: EditToolbarMeasuredWidths = {
  icon: 32,
  format: 120,
  resolution: 88,
  divider: 9,
}

export type EditToolbarFit = {
  hidden: Set<EditOverflowKey>
  showResolution: boolean
  showFormat: boolean
}

export function isEditOverflowKey(value: string): value is EditOverflowKey {
  return (EDIT_OVERFLOW_DROP_ORDER as readonly string[]).includes(value)
}

export function editOverflowSetsEqual(a: Set<EditOverflowKey>, b: Set<EditOverflowKey>): boolean {
  if (a.size !== b.size) return false
  for (const key of a) {
    if (!b.has(key)) return false
  }
  return true
}

function widthOf(key: string, widths: EditToolbarMeasuredWidths): number {
  if (key === "format") return widths.format
  if (key === "resolution") return widths.resolution
  if (key === "div1" || key === "div2") return widths.divider
  return widths.icon
}

function visibleKeys(hidden: Set<string>, showResolution: boolean, showAi: boolean, showFormat = true): string[] {
  const keys: string[] = ["select", "pen", "zoomIn", "zoomOut", "pan"]
  for (const key of ["eraser", "shape", "arrow", "arrowLabel", "text"] as const) {
    if (!hidden.has(key)) keys.push(key)
  }
  const history: string[] = []
  if (!hidden.has("undo")) history.push("undo")
  if (!hidden.has("redo")) history.push("redo")
  if (history.length > 0) keys.push("div1", ...history)
  if (showAi) keys.push("ai")
  if (showFormat) keys.push("div2", "format")
  if (showResolution) keys.push("resolution")
  if (!hidden.has("download")) keys.push("download")
  if (!hidden.has("copy")) keys.push("copy")
  return keys
}

export function editToolbarRowWidth(
  hidden: Set<string>,
  showResolution: boolean,
  showMore: boolean,
  widths: EditToolbarMeasuredWidths = FALLBACK_EDIT_TOOLBAR_WIDTHS,
  showAi = true,
  showFormat = true,
): number {
  const keys = visibleKeys(hidden, showResolution, showAi, showFormat)
  if (keys.length === 0) return showMore ? EDIT_TOOLBAR_MORE_RESERVE_PX : 0
  let width = 0
  for (let i = 0; i < keys.length; i++) {
    if (i > 0) width += EDIT_TOOLBAR_GAP_PX
    width += widthOf(keys[i], widths)
  }
  if (showMore) width += EDIT_TOOLBAR_MORE_RESERVE_PX
  return width
}

export function computeEditToolbarFit(
  available: number,
  hasResolution: boolean,
  widths: EditToolbarMeasuredWidths = FALLBACK_EDIT_TOOLBAR_WIDTHS,
  showAi = true,
): EditToolbarFit {
  const hidden = new Set<EditOverflowKey>()
  const fits = (showResolution: boolean, showFormat: boolean) =>
    editToolbarRowWidth(hidden, showResolution, hidden.size > 0 || !showFormat, widths, showAi, showFormat) <=
    available + 0.5

  const result = (showResolution: boolean, showFormat: boolean): EditToolbarFit => ({
    hidden,
    showResolution,
    showFormat,
  })

  if (hasResolution && fits(true, true)) return result(true, true)
  if (!hasResolution && fits(false, true)) return result(false, true)

  if (hasResolution && fits(true, false)) return result(true, false)
  if (fits(false, false)) return result(false, false)

  for (const key of EDIT_OVERFLOW_DROP_ORDER) {
    hidden.add(key)
    if (fits(false, false)) return result(false, false)
  }
  return result(false, false)
}
